import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Check, ChevronDown } from 'lucide-react'

export interface SelectOption {
  id: string
  label: string
  /** Optional second line in the menu: what picking this option actually does. */
  hint?: string
}

export interface SelectMenuProps {
  /** Muted caption inside the trigger, so the control says what it controls. */
  label: string
  options: SelectOption[]
  value: string
  onPick: (id: string) => void
  /** Anchor the menu to the trigger's right edge. Default, since this sits right-aligned. */
  align?: 'left' | 'right'
}

/**
 * A dropdown for a one-of-N choice.
 *
 * Built rather than using a native <select> because the menu carries a hint line per
 * option -- "Postgres, midnight to now" against "SSE tape, no history" is the difference
 * between two windows that otherwise look like synonyms, and a native option element
 * cannot show it.
 *
 * That choice costs the three behaviours a native select gives away, so they are
 * implemented here rather than skipped: Escape closes, a click anywhere outside closes,
 * and the trigger reports state through aria-haspopup/aria-expanded with the list as a
 * listbox. A custom dropdown missing those is a button that traps the operator.
 */
export function SelectMenu({ label, options, value, onPick, align = 'right' }: SelectMenuProps) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const active = options.find((o) => o.id === value)

  return (
    <div className="relative shrink-0" ref={root}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          'flex h-9 items-center gap-2.5 rounded-[12px] border px-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
          open
            ? 'border-[rgba(15,23,42,0.18)] bg-white'
            : 'border-[rgba(15,23,42,0.08)] bg-white hover:border-[rgba(15,23,42,0.16)]',
        )}
      >
        <span className="flex flex-col items-start leading-none">
          <span className="text-[9px] font-medium uppercase tracking-[0.14em] text-[#94A3B8]">
            {label}
          </span>
          <span className="mt-0.5 text-[13px] font-semibold text-[#0F172A]">
            {active?.label ?? value}
          </span>
        </span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 shrink-0 text-[#94A3B8] transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className={clsx(
            'absolute z-40 mt-1.5 w-[260px] overflow-hidden rounded-[14px] border border-[rgba(15,23,42,0.08)] bg-white py-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.14)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          <li className="px-3 pb-1.5 pt-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#94A3B8]">
            Choose window
          </li>
          {options.map((o) => {
            const isActive = o.id === value
            return (
              <li key={o.id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => { onPick(o.id); setOpen(false) }}
                  className={clsx(
                    'flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:bg-[rgba(15,23,42,0.05)]',
                    isActive ? 'bg-[rgba(37,99,235,0.07)]' : 'hover:bg-[rgba(15,23,42,0.04)]',
                  )}
                >
                  <Check
                    className={clsx(
                      'mt-0.5 h-3.5 w-3.5 shrink-0',
                      isActive ? 'text-[#2563EB]' : 'text-transparent',
                    )}
                  />
                  <span className="min-w-0 leading-tight">
                    <span className={clsx(
                      'block text-[13px]',
                      isActive ? 'font-semibold text-[#0F172A]' : 'font-medium text-[#334155]',
                    )}>
                      {o.label}
                    </span>
                    {o.hint ? (
                      <span className="mt-0.5 block text-[11px] text-[#94A3B8]">{o.hint}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
